import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { withLoading } from '@ps/core-overlay';

import {
  SurveyPerformanceBuyer,
  SurveyPerformanceFilters,
  SurveyPerformanceResult,
} from '../models/survey-performance-report.model';
import { SurveyPerformanceBuyerResponseDto } from './dtos/survey-performance-buyer.dto';
import { SurveyPerformanceReportResponseDto } from './dtos/survey-performance-report.dto';
import { SurveyPerformanceReportMapper } from './mappers/survey-performance-report.mapper';
import { SurveyPerformanceReportPayloadMapper } from './mappers/survey-performance-report-payload.mapper';

@Injectable({ providedIn: 'root' })
export class SurveyPerformanceReportApi {
  private readonly _config = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = this._config.pureSpectrum.url;
  private readonly _mapper = new SurveyPerformanceReportMapper();
  private readonly _payloadMapper = new SurveyPerformanceReportPayloadMapper();

  public buyers(): Observable<SurveyPerformanceBuyer[]> {
    return this._http
      .get<SurveyPerformanceBuyerResponseDto>(
        `${this._baseUrl}/invoice/companies`,
        { params: { isSpplr: false, isBuyr: true } },
      )
      .pipe(
        map((response) =>
          response.companies.map((company) => ({
            id: company.id,
            name: company.name,
          })),
        ),
      );
  }

  public results(
    filters: SurveyPerformanceFilters,
  ): Observable<SurveyPerformanceResult[]> {
    const params = new HttpParams({
      fromObject: {
        startDate: filters.startDate,
        endDate: filters.endDate,
        country_code: filters.countryCode,
      },
    });

    return this._http
      .post<SurveyPerformanceReportResponseDto>(
        `${this._baseUrl}/getPerformanceReport`,
        this._payloadMapper.toPayload(filters),
        {
          params,
          context: withLoading('page'),
        },
      )
      .pipe(
        map((response) => {
          if (!response.result) {
            throw new Error(response.msg ?? 'No results found');
          }
          return this._mapper.fromDtoList(response.result);
        }),
      );
  }
}
